import { ActionBar, Button } from '@components'
import { AccountType } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { setTagsInFilter } from '@utils'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa'
import { AccountFormModal } from './AccountFormModal'
import { IFilter, IFilterButton } from '@root/components/ActionBar/FilterBar/FilterSelection'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'

interface Props {
  onSearch: (value: string) => void
  onFilter: (value: IFilter[]) => void
  activeFilters: IFilter[]
}

export const AccountActionBar: React.FC<Props> = ({ onSearch, onFilter, activeFilters }) => {
  const [t] = useTranslation()
  const [isFormVisible, setFormVisible] = useState(false)
  const { data: business } = useBusinessContext()
  const hasEditPermission = usePermission(PermissionSet.ACCOUNTS_EDIT)

  const filterButtons = React.useMemo<IFilterButton[]>(
    () => [
      {
        id: 'is_shown',
        name: t('accounts.actionBar.filterIsShown'),
        filter: { is_shown: true }
      },
      {
        id: 'is_used',
        name: t('accounts.actionBar.filterIsUsed'),
        filter: { is_used: true }
      },
      {
        id: 'bank',
        name: t('accounts.actionBar.filterBank'),
        filter: { type: [AccountType.ASS_PAY] }
      },
      {
        id: 'revenue',
        name: t('accounts.actionBar.filterRev'),
        filter: { type: [AccountType.REV, AccountType.REV_SAL, AccountType.REV_NO] }
      },
      {
        id: 'expense',
        name: t('accounts.actionBar.filterExp'),
        filter: { type: [AccountType.EXP, AccountType.EXP_NO, AccountType.EXP_50] }
      },
      {
        id: 'taxes',
        name: t('accounts.actionBar.filterTax'),
        filter: {
          type: [AccountType.EXP_TAX, AccountType.LIA_VAT, AccountType.ASS_VAT]
        }
      }
    ],
    []
  )

  const filterInputs = React.useMemo(
    () => [
      ...(business?.has_tags
        ? [
            <TagSelector
              label={t('components.tagFilterSelector.label')}
              key={'tags_filter_selector'}
              value={activeFilters.filter(value => 'tags' in value).map(value => value.tags)[0]}
              onChange={(tagIds: number[]) => {
                const updatedFilters = setTagsInFilter(activeFilters, tagIds)
                onFilter(updatedFilters)
              }}
            />
          ]
        : [])
    ],
    [activeFilters]
  )

  const primaryButtons = [
    <Button key="account-main-action" onClick={() => setFormVisible(true)} icon={<FaPlus />}>
      {t('accounts.actionBar.newAccount')}
    </Button>
  ]

  return (
    <>
      <ActionBar
        searchPlaceholder={t('accounts.actionBar.search')}
        onSearch={search => onSearch(search)}
        onFilter={filters => onFilter(filters)}
        filterButtons={filterButtons}
        filterInputs={filterInputs}
        activeFilters={activeFilters}
        primaryButtons={hasEditPermission && primaryButtons}
      />
      <AccountFormModal isVisible={isFormVisible} handleOnClose={() => setFormVisible(false)} />
    </>
  )
}
